import { useLoaderData } from 'react-router';
import { AddOnVisibilityOperationType } from '../../../../common/enums';
import { useAddOnVisibility } from '../../../../common/hooks/useAddOnVisibility';
import { Loading } from '../../../Loading';
import {
  AdditionalServicesCheckoutContent,
  getAddOnItemsFromCodes,
} from '../../../OrderSubscriptionConfiguration/AdditionalServicesCheckoutContent';
import { getCommercialProductForGuid } from '../../../ShoppingBasket/shoppingBasketUtils';
import { getBaseRules, isMobileVoiceAddOn } from '../../../SubscriptionDetails/addOnDependencyUtils';
import type { PlaceOrderLoaderData } from '../../../../common/loaders';
import type { AddedAddon } from '../../../../common/types/addon';
import type { ChildOrderItem } from '../../../../generated/api/childOrderItem';
import type { OnlineModel } from '../../../../generated/api/onlineModel';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { BaseRules } from '../../../SubscriptionDetails/addOnDependencyUtils';
import type { PlaceOrderItemUnit } from '../../placeOrderTypes';

import './SubscriptionsAddonsView.scss';

export interface UseHasSubscriptionAddonsParams {
  commercialProductCode: string;
  mdmId: string;
}

export const useHasSubscriptionAddons = ({ commercialProductCode, mdmId }: UseHasSubscriptionAddonsParams) => {
  const { addOnVisibilities, addOnVisibilitiesLoading } = useAddOnVisibility(
    commercialProductCode,
    AddOnVisibilityOperationType.NEW_SALES,
    undefined,
    mdmId
  );

  return {
    hasSubscriptionAddons: addOnVisibilities?.some(v => v.selfServiceEditable) ?? false,
    isLoading: addOnVisibilitiesLoading,
  };
};

export interface SubscriptionsAddonsViewProps {
  mdmId: string;
  commercialProductCode: string;
  onlineModel: OnlineModel;
  itemInstanceId: string;
  orderItem: PlaceOrderItemUnit;
  onAddOnsUpdate: (addons: ChildOrderItem[]) => void;
}

export const SubscriptionsAddonsView = ({
  mdmId,
  commercialProductCode,
  onlineModel,
  itemInstanceId,
  orderItem,
  onAddOnsUpdate,
}: SubscriptionsAddonsViewProps) => {
  // Get add-on rules from loader data (already loaded once before component mount)
  const { addOnRules } = useLoaderData() as PlaceOrderLoaderData;
  const associations = (addOnRules?.associations?.[0] as AssociationRecord[]) ?? [];
  const dependencies = (addOnRules?.dependencies?.[0] as DependencyRecord[]) ?? [];

  const { addOnVisibilities } = useAddOnVisibility(
    commercialProductCode,
    AddOnVisibilityOperationType.NEW_SALES,
    undefined,
    mdmId
  );
  const commercialProduct = getCommercialProductForGuid(commercialProductCode, onlineModel);

  if (!commercialProduct) {
    return <Loading />;
  }

  const baseRules: BaseRules = getBaseRules(commercialProduct, associations, dependencies);

  // Get visible add-on codes from visibility API
  const visibleAddOnCodes = addOnVisibilities?.map(v => v.addOnGuidCode) ?? [];

  const getAddedAddOns = (): AddedAddon[] => {
    // Only return add-ons that are:
    // 1. Already selected in the order item
    // 2. AND visible/editable according to visibility API
    // Bundled add-ons that aren't user-editable should not be passed as productAddOns
    const existingAddOnCodes = orderItem.addons
      .map(addon => addon.addOnCode)
      .filter(code => visibleAddOnCodes.includes(code));

    // Restore voicemail language from basket-stored addOnAttributes
    const voicemailAddon = orderItem.addons.find(
      addon => isMobileVoiceAddOn(addon.addOnCode) && addon.addOnAttributes?.language
    );
    const storedLanguage = voicemailAddon?.addOnAttributes?.language;

    return getAddOnItemsFromCodes(existingAddOnCodes, [], baseRules, storedLanguage);
  };

  // Get hidden addons - addons that are not in the visibility list (bundled add-ons like opening fees)
  const getHiddenAddons = (): ChildOrderItem[] => {
    return orderItem.addons.filter(addon => !visibleAddOnCodes.includes(addon.addOnCode));
  };

  const handleAddOnSelectionChange = (_cartIndex: string, addedAddOns: AddedAddon[]) => {
    const visibleAddons: ChildOrderItem[] = addedAddOns.map(addon => ({
      addOnCode: addon.addOnCode,
      addOnAssociationCode: addon.addOnAssociationCode,
      addOnAttributes: addon.addOnAttributes,
    }));
    // Preserve hidden addons (like Reissunetti2) that are not visible in the form
    const hiddenAddons = getHiddenAddons();
    onAddOnsUpdate([...hiddenAddons, ...visibleAddons]);
  };

  return (
    <AdditionalServicesCheckoutContent
      commercialProduct={commercialProduct}
      cartItemInstanceId={itemInstanceId}
      productAddOns={getAddedAddOns()}
      onAddOnSelectionChange={handleAddOnSelectionChange}
      preFetchedAddOnVisibilities={addOnVisibilities}
      hideHeader={false}
      addOnVisibilityOperationType={AddOnVisibilityOperationType.NEW_SALES}
      addOnAssociations={associations}
      addOnDependencies={dependencies}
    />
  );
};
